import {
  GetVisitDetailsQuery,
  SubmitVisitNoteMutation,
  useGetVisitDetailsQuery,
  useUpdateVisitResourceStatusMutation,
  VisitResourceVirtualProviderStatusEnum as VirtualStatusEnum,
} from "~/graphql/__generated__/graphql";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import VisitStatusChip from "./VisitStatusChip";
import { HomeHealthVisitConfirmation } from "~/features/home-health-demo";
import VisitEventLogTab from "./VisitEventLog";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import { merge, orderBy } from "lodash";
import { Fragment, useCallback, useState } from "react";
import { formatRelative } from "date-fns";
import Close from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";

import { Permission } from "~/hooks/permissions";
import { CommandCenterLocationState } from "~/components/command/DateSelector";
import PatientIdentifiers from "~/components/common/PatientIdentifiers";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import VisitDetailsTab from "./VisitDetailsTab";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import VisitPopupActions from "./VisitPopupActions";
import CallCompletionPrompt from "./CallCompletionPrompt";
import { useWindowFocus } from "~/hooks/useWindowFocus";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import DebugSetNotifiedButton from "./DebugSetNotifiedButton";

type Visit = GetVisitDetailsQuery["visitSingle"];

interface Props {
  visit: { id: number };
  visitResourceId?: number;
  onClose: () => void;
  onCancel: (visitId: number) => void;
  onEdit: (visitId: number) => void;
}

const sameAsPreviousEvent = (previous: NonNullable<Visit>["eventLog"][number], e: NonNullable<Visit>["eventLog"][number]) => {
  return previous
    && e.logEntryType === previous.logEntryType
    && e.event === previous.event
    && e.visitResourceId === previous.visitResourceId
    && new Date(e.eventTime).getTime() - new Date(previous.eventTime).getTime() < 15 * 1000;
}

export default function VisitPopup({
  visit,
  visitResourceId,
  onClose,
  onCancel,
  onEdit,
}: Props) {
  const { cache } = useApolloClient();
  const location = useLocation();

  const [currentTab, setCurrentTab] = useState("details");
  const [showCompletionPrompt, setShowCompletionPrompt] = useState(false);

  const { timeLabelWithZone } = useZonedDateFormat();
  const providerId = useCurrentOrg()?.providerId;

  const { data: data, loading: loading } = useGetVisitDetailsQuery(
    {
      variables: {
        visitId: visit.id,
      },
      pollInterval: 1_000 * 15, // poll every 15 seconds,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "network-only",
    }
  );

  const detailedVisit = merge({}, visit, data?.visitSingle);

  const sortedNotes = orderBy(data?.visitSingle?.notes || [], "createdAt", "desc");

  // Window focus detection for call completion prompt
  const [updateResourceStatus] = useUpdateVisitResourceStatusMutation();
  const currentProviderResource = detailedVisit.visitResources?.find(vr => vr.providerId === providerId);
  const isCallActive = currentProviderResource?.virtualProviderStatus === VirtualStatusEnum.Joined;

  // Detect when user returns to window during active call
  useWindowFocus(useCallback(() => {
    if (isCallActive && !showCompletionPrompt) {
      setShowCompletionPrompt(true);
    }
  }, [isCallActive, showCompletionPrompt]), isCallActive);

  // Handle call completion
  const handleCallComplete = useCallback(async () => {
    if (!currentProviderResource) return;

    await updateResourceStatus({
      variables: {
        id: currentProviderResource.id,
        visitId: visit.id,
        newStatus: VirtualStatusEnum.Complete,
      },
      refetchQueries: "active",
      awaitRefetchQueries: true,
    });
    setShowCompletionPrompt(false);
  }, [currentProviderResource, visit.id, updateResourceStatus]);

  // Handle dismissing the prompt
  const handleDismissPrompt = useCallback(() => {
    setShowCompletionPrompt(false);
  }, []);

  // Due to network issues, it is possible the client will update the event status the same way multiple times within a couple
  // of seconds. We filter this out display side, rather than when recording, because "exactly once" is complicated in
  // an async, parallel environment (eg, with multiple lambdas), while "only render the first" is easy.
  const events = data?.visitSingle?.eventLog ?? [];
  const sortedEvents = orderBy(events, "eventTime", "desc");
  const filteredEventLog = sortedEvents.filter((e, index) => !sameAsPreviousEvent(sortedEvents[index - 1], e));

  const [submitNoteMutation, { loading: submitting }] =
    useMutation<SubmitVisitNoteMutation>(SUBMIT_VISIT_NOTE_MUTATION);
  const onNoteSubmit = async (noteBody: string) => {
    await submitNoteMutation({
      variables: {
        visitId: visit.id,
        body: noteBody,
      },
    });
    cache.evict({
      id: `VisitSelectItem:${visit.id}`,
      fieldName: "notes",
    });
    cache.gc();
  };

  const patientName = `${detailedVisit.patient?.firstName || ""} ${detailedVisit.patient?.lastName || ""}`;
  const patientLink = location.pathname.startsWith(`/patients/${detailedVisit.patient?.id}`) ? patientName : <Link to={`/patients/${detailedVisit.patient?.id}`}>
    {patientName}
  </Link>;

  const visitTime = detailedVisit.startTime && timeLabelWithZone(detailedVisit.startTime, true, detailedVisit.addressTimezone);
  const state: CommandCenterLocationState = {
    date: new Date(detailedVisit.startTime),
    // Arbitrarily link to the first visit resource, so that when we highlight it on screen,
    // so that there aren't two elements competing for attention. See components/command/VisitEvent
    // for the "receiving end" of this location.state.
    visitResourceId: visitResourceId || detailedVisit.visitResources?.[0]?.id,
  };

  const visitLink = location.pathname.startsWith(`/command`) ? visitTime : <Link to={`/command`} state={state}>
    {visitTime}
  </Link>;

  const currentVirtuals = detailedVisit.visitResources?.filter(vr => vr.virtualProviderStatus === VirtualStatusEnum.Notified || vr.virtualProviderStatus === VirtualStatusEnum.Joined) || [];

  return (
    <Card elevation={3} sx={{ width: 700 }}>
      <CardHeader
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          flex: 1,
          flexDirection: "row",
        }}
        action={
          <IconButton aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        }
        title={
          <Typography variant="h6" component="div">
            {patientLink} - {visitLink}
            {detailedVisit.eventStatus && <VisitStatusChip
              status={detailedVisit.eventStatus}
              cancellationReason={detailedVisit.cancellationReason?.description}
              sx={{ ml: 2, verticalAlign: "text-top" }}
            />}
            {currentVirtuals.map(vr => <VisitStatusChip
              status={vr.virtualProviderStatus!}
              about="visit_resource"
              subject={vr?.provider}
              sx={{ ml: 2, verticalAlign: "text-top" }}
            />)}
            {detailedVisit.visitWarningLogId && <Chip sx={{ ml: 2, borderRadius: "4px", fontSize: "13px", fontWeight: 500, verticalAlign: "text-top" }} size='small' label={'Manual Override'} />}
            <HomeHealthVisitConfirmation visitId={visit.id} />
            <PatientIdentifiers patient={detailedVisit.patient} />
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Grid container direction="row" columnSpacing={1}>
          <Grid size={{ sm: 3 }}>
            <Tabs
              orientation="vertical"
              variant="standard"
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
            >
              <Tab value="details" label="Details" />
              <Tab value="patient" label="Patient Notes" />
              <Tab value="notes" label={`Visit Notes (${loading ? "..." : sortedNotes.length})`}
              />
              <Tab value="events" label={`Events (${loading ? "..." : filteredEventLog.length})`}
              />
              {
                // @ts-expect-error - window.debugMode is a global variable
                window.debugMode && <Tab value="debug" label="Debug" />
              }
            </Tabs>
          </Grid>
          <Grid size={{ sm: 9 }} sx={{ mt: 1 }}>
            <Box sx={{ overflow: "auto", width: "100%", height: 520 }}>
              {currentTab === "details" && <VisitDetailsTab visit={detailedVisit} loading={loading} showErrands />}
              {currentTab === "patient" && detailedVisit.patient && <PatientTab visit={detailedVisit} />}
              {currentTab === "notes" && <NotesTab
                notes={sortedNotes}
                loading={loading}
                onSubmit={onNoteSubmit}
                submitting={submitting}
              />}
              {currentTab === "events" && (
                <VisitEventLogTab
                  events={filteredEventLog}
                  loading={loading}
                  visit={detailedVisit}
                />
              )}
              {currentTab === "debug" && <DebugTab visit={detailedVisit} providerId={providerId} />}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <VisitPopupActions isLoading={loading} visit={detailedVisit} onCancel={onCancel} onEdit={onEdit} />
      <CallCompletionPrompt
        open={showCompletionPrompt}
        patientName={patientName}
        onComplete={handleCallComplete}
        onDismiss={handleDismissPrompt}
      />
    </Card>
  );
}

type NotesTabProps = {
  notes: NonNullable<Visit>["notes"];
  loading: boolean;
  onSubmit: (noteBody: string) => void | Promise<void>;
  submitting: boolean;
};

const NotesTab = ({ notes, loading, onSubmit, submitting }: NotesTabProps) => {
  const { register, handleSubmit, watch, reset } = useForm();
  const noteBody = watch("noteBody");
  const submitEnabled = !!noteBody && !submitting;

  const submit = async () => {
    if (submitEnabled) {
      await onSubmit(noteBody);
      reset();
    }
  };

  return (
    <Box sx={{ display: 'flex' }} flexDirection="column" height="100%" gap={2}>
      <PermissionOrHide permission={Permission.manageAnyVisit}>
        <Box sx={{ flexShrink: 1 }}>
          <form id="new-note-form" onSubmit={handleSubmit(submit)}>
            <FormControl fullWidth margin="dense" disabled={submitting}>
              <InputLabel htmlFor="new-note">Add a note</InputLabel>
              <OutlinedInput
                id="new-note"
                {...register("noteBody")}
                fullWidth
                label="Add a note"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="submit note"
                      type="submit"
                      form="new-note-form"
                      disabled={!submitEnabled}
                      edge="end"
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </form>
        </Box>
      </PermissionOrHide>
      <Box sx={{ flexGrow: 1 }}>
        {loading && (
          <>
            <Skeleton
              variant="text"
              height={10}
              sx={{ width: "60%", mb: 0.5 }}
            />
            <Skeleton variant="text" height={10} sx={{ width: "80%" }} />
          </>
        )}
        {!loading && notes.length === 0 && (
          <Typography variant="body2">No notes found.</Typography>
        )}
        {notes.map((note) => (
          <Fragment key={note.id}>
            <Typography variant="body1">
              {note.user?.firstName} {note.user?.lastName} - {formatRelative(note.createdAt, new Date())}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {note.body}
            </Typography>
          </Fragment>
        ))}
      </Box>
    </Box>
  );
};

type DebugTabProps = {
  visit: NonNullable<Visit>;
  providerId: number | null | undefined;
};
const DebugTab = ({ visit, providerId }: DebugTabProps) => {
  const { timeLabelWithZone } = useZonedDateFormat();
  const currentProviderResource = visit.visitResources?.find(vr => vr.providerId === providerId);

  return (
    <>
      <Typography variant="button">ID</Typography>
      <Typography variant="body1" gutterBottom>
        {visit.id}
      </Typography>

      <Typography variant="button" sx={{ mt: 2, display: 'block' }}>
        Debug Mode Status
      </Typography>
      <Typography variant="body2" gutterBottom>
        {/* @ts-expect-error - window.debugMode is a global variable */}
        Debug mode enabled: <strong>{window.debugMode ? 'YES' : 'NO'}</strong>
      </Typography>
      <Typography variant="body2" gutterBottom>
        Your provider ID: <strong>{providerId || 'Not logged in as provider'}</strong>
      </Typography>

      <Typography variant="button" sx={{ mt: 2, display: 'block' }}>
        Event Log Permissions
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
        To advance status and delete events, you need the proper permissions assigned to your user role in the database.
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Note: These permissions are controlled by your organization admin and cannot be overridden in debug mode for security reasons.
      </Typography>

      {currentProviderResource ? (
        <>
          <Typography variant="button" sx={{ mt: 2, display: 'block' }}>
            Virtual Provider Status Controls
          </Typography>
          <Typography variant="body2" gutterBottom>
            Visit Resource ID: <strong>{currentProviderResource.id}</strong>
          </Typography>
          <Typography variant="body2" gutterBottom>
            Current status: <strong>{currentProviderResource.virtualProviderStatus || 'null'}</strong>
          </Typography>
          <Typography variant="body2" gutterBottom>
            Resource has invitedToVisit: <strong>{currentProviderResource.resourceRequirement?.invitedToVisit ? 'YES' : 'NO'}</strong>
          </Typography>
          <Box sx={{ mt: 1, mb: 2 }}>
            <DebugSetNotifiedButton
              visitResourceId={currentProviderResource.id}
              visitId={visit.id}
              currentStatus={currentProviderResource.virtualProviderStatus}
            />
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="error" gutterBottom>
          No virtual provider resource found for your provider ID.
          You must be logged in as Billy Black (provider ID: 10005)
        </Typography>
      )}

      <Typography variant="button" sx={{ mt: 2, display: 'block' }}>Scheduler Log</Typography>
      <Typography variant="body1" gutterBottom>
        <Link to={`/scheduler-log/${visit.id}`}>Go to Scheduler Log</Link>
      </Typography>

      <Typography variant="button">Time</Typography>
      <Typography variant="body1" gutterBottom>
        {timeLabelWithZone(visit.startTime, true, visit.addressTimezone)}{" "}
        -&nbsp;
        {timeLabelWithZone(visit.endTime, true, visit.addressTimezone)}
      </Typography>

      <Typography variant="button">Arrival window</Typography>
      <Typography variant="body1" gutterBottom>
        {timeLabelWithZone(visit.arrivalWindowStartTime, true, visit.addressTimezone)}{" "}
        -&nbsp;
        {timeLabelWithZone(visit.arrivalWindowEndTime, true, visit.addressTimezone)}
      </Typography>

      <Typography variant="button">Status</Typography>
      <Typography variant="body1" gutterBottom>
        {visit.eventStatus}
      </Typography>

      <Typography variant="button">Manually edited</Typography>
      <Typography variant="body1" gutterBottom>
        {visit.manualEdits ? "Yes" : "No"}
      </Typography>

      <Typography variant="button">Visit Resources</Typography>
      <Typography variant="body1" gutterBottom component="div">
        <ul style={{ marginTop: 0, marginBottom: 0 }}>
          {visit.visitResources?.map((visitResource) => (
            <li key={visitResource.id}>
              {visitResource.id}
              <ul style={{ marginTop: 0, marginBottom: 0 }}>
                <li>
                  {visitResource.provider?.firstName}{" "}
                  {visitResource.provider?.lastName} ({visitResource.providerId}
                  )
                </li>
                <li>
                  {timeLabelWithZone(
                    visitResource.startTime,
                    true,
                    visit.addressTimezone
                  )}{" "}
                  -&nbsp;
                  {timeLabelWithZone(
                    visitResource.endTime,
                    true,
                    visit.addressTimezone
                  )}
                </li>
                <li>Start of shift drive time: {visitResource.startOfShiftDriveTime}</li>
                <li>Inter visit drive time: {visitResource.interVisitDriveTime}</li>
                <li>End of shift drive time: {visitResource.endOfShiftDriveTime}</li>
              </ul>
            </li>
          ))}
        </ul>
      </Typography>
    </>
  );
};

type PatientTabProps = {
  visit: NonNullable<Visit>;
}

const PatientTab = ({ visit }: PatientTabProps) => {
  return (
    <Box sx={{ maxWidth: '320px', mt: 2 }}>
      <Typography variant="body1" gutterBottom sx={{ ml: 2 }}>
        {visit.patient?.patientNotes || "No patient notes"}
      </Typography>
    </Box>
  )
}


export const GET_VISIT_DETAILS_QUERY = gql`
  query GetVisitDetails($visitId: Int!)
  @permission(requires: useWebApp)
  {
    visitSingle(where: { id: { eq: $visitId } }) {
      id
      careRequirementId
      startTime
      endTime
      eventStatus
      addressTimezone
      serviceInstructions
      addressStreet
      addressCity
      addressState
      addressZip
      arrivalWindowStartTime
      arrivalWindowEndTime
      manualEdits
      patient {
        id
        firstName
        lastName
        dateOfBirth
        sex
        phone
        patientNotes
        serviceInstructions

        patientIdentifiers {
          identifier
          identifier_type
        }
        careTeamProviders {
          id
          providerId
        }
      }
      visitTypeId
      visitResources {
        id
        startTime
        endTime
        providerId
        errandStatus
        addressStreet
        addressLineTwo
        addressCity
        addressState
        addressZip
        addressCoordinates
        addressTimezone
        startOfShiftDriveTime
        interVisitDriveTime
        endOfShiftDriveTime
        visitResourceRequirementId
        virtualProviderStatus
        provider {
          id
          firstName
          lastName
          providerType {
            id
            name
          }
        }
        resourceRequirement {
          invitedToVisit
          errandType {
            id
            name
          }
        }
      }
      notes {
        id
        body
        createdAt
        user {
          id
          email
          firstName
          lastName
        }
      }
      visitServices {
        id
        service {
          id
          name
        }
      }
      manualEdits
      visitWarningLogId
      cancellationReason {
        id
        description
      }
      episode {
        id
        serviceLineId
        organizationalUnitId
        precautions {
          id
          precautionId
        }
      }
      eventLog {
        id
        eventTime
        logEntryType
        event
        autoGenerated
        visitResourceId
        createdByUser {
          firstName
          lastName
        }
      }
    }
  }
`;

const SUBMIT_VISIT_NOTE_MUTATION = gql`
  mutation SubmitVisitNote($visitId: Int!, $body: String!)
  @permission(requires: manageAnyVisit)
  {
    createVisitNote(visitId: $visitId, body: $body) {
      id
      body
      createdAt
      user {
        id
        email
      }
    }
  }
`;
